import * as Sentry from '@sentry/vue'
import type { BrowserOptions, ErrorEvent, EventHint } from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { isStaleChunkError } from '@/utils/chunkLoadRecovery'

/**
 * Error reporting. Only in production builds with an `https://` VITE_APP_SENTRY_DSN; otherwise
 * nothing initialises.
 *
 * The portal shows PHI on screen, so session replay masks all text and blocks all media.
 * Never relax either. Route params and query strings (patient, complaint and case IDs, task
 * keys) are stripped from every event before it leaves the browser.
 *
 * Sentry captures unhandled rejections and router errors itself (the Vue router integration adds
 * its own `router.onError`), so the app does not report those again.
 */

/** @sentry/vue does not re-export this type from @sentry/core. */
type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0]

/** Span attribute prefixes the Vue router integration fills from `to.params` and `to.query`. */
const ROUTE_VALUE_PREFIXES = ['query.', 'params.', 'url.path.parameter.', 'url.path.params.']
/** Span attributes that hold a full URL; their query string and hash are dropped. */
const URL_KEYS = ['url', 'url.full', 'http.url']

function withoutQuery(url: string): string {
  return url.split(/[?#]/)[0]
}

function scrubData(data: Record<string, unknown> | undefined): void {
  if (!data) return
  for (const key of Object.keys(data)) {
    if (ROUTE_VALUE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      delete data[key]
    } else if (URL_KEYS.includes(key) && typeof data[key] === 'string') {
      data[key] = withoutQuery(data[key])
    }
  }
}

function scrubRequestUrl(event: ErrorEvent | TransactionEvent): void {
  if (event.request?.url) event.request.url = withoutQuery(event.request.url)
  if (event.request?.query_string) delete event.request.query_string
}

/** beforeSend: stale chunks after a deploy are expected (the app reloads), so they are dropped. */
export function filterErrorEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  if (isStaleChunkError(hint.originalException)) return null
  scrubRequestUrl(event)
  return event
}

/** beforeSendTransaction: drops route param and query values from the transaction and its spans. */
export function scrubTransactionEvent(event: TransactionEvent): TransactionEvent {
  scrubData(event.contexts?.trace?.data)
  for (const span of event.spans ?? []) scrubData(span.data)
  scrubRequestUrl(event)
  return event
}

export function initSentry(app: App, router: Router): void {
  const dsn = import.meta.env.VITE_APP_SENTRY_DSN?.trim()
  // An unset Azure pipeline variable arrives as the literal text `$(VITE_APP_SENTRY_DSN)`.
  if (!dsn?.startsWith('https://') || !import.meta.env.PROD) return

  Sentry.init({
    // Passing `app` wraps app.config.errorHandler, so main.ts sets that first.
    app,
    dsn,
    environment: import.meta.env.VITE_APP_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      })
    ],
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    // Sentry 11 streams spans by default and then never calls beforeSendTransaction, so the route
    // param / query scrubbing below would silently not run. Send whole transactions instead.
    traceLifecycle: 'static',
    beforeSend: filterErrorEvent,
    beforeSendTransaction: scrubTransactionEvent
  })
}
